import { useState, useEffect, useContext, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { securityCheck } from "../../utils/authHelpers";
import { formatTitleCase } from "../../utils/formatHelpers";
import { RegionService } from "../../services/RegionService";
import { CarrierService } from "../../services/CarrierService";
import { AlertContext } from "../../components/Dashboard/Dashboard";

export default function useOfferBoardsReceiver(activeUser, userPermissions, isLoggedIn) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  let [searchParams] = useSearchParams();
  const [connection, setConnection] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [regions, setRegions] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [carrierLoads, setCarrierLoads] = useState([]);
  const [newItem, setNewItem] = useState({});
  const [pendingLoadsCount, setPendingLoadsCount] = useState(null);
  const [refreshLoads, setRefreshLoads] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(null);
  const [isRepUser, setIsRepUser] = useState(null);
  const [carrierName, setCarrierName] = useState("");

  // Fetches the list of available customers
    const getCustomers = useCallback(() => {
    const storedCustomerData = localStorage.getItem("customerData");
    if (storedCustomerData) {
      try {
        const parsedCustomers = JSON.parse(storedCustomerData);
        setCustomers(parsedCustomers);
      } catch (e) {
        console.error("Failed to parse customerData from localStorage", e);
        warning("Could not load customer filter options.");
        setCustomers([]);        
      }
    } else {
      setCustomers([]);      
    }
  }, [warning]); // setCustomers is stable from useState

   const getLoadsByRegion = useCallback(() => {
    if (selectedRegions.length > 0) {
      const customersParam = selectedCustomers.length > 0 ? selectedCustomers.join(",") : null;

      CarrierService.GetLoadsByRegion(instance, selectedRegions.join(","), customersParam).then(res => {
        setCarrierLoads(res);
        let pendingCount = res.filter(({ status }) => status === "pending").length;
        setPendingLoadsCount(pendingCount > 0 ? pendingCount : null);
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
      } else if (isAdminUser) { // If admin and no regions selected, clear loads and stop loading
      setCarrierLoads([]);
      setPendingLoadsCount(null);
      setIsLoading(false);
    }
  }, [instance, selectedRegions, selectedCustomers, isAdminUser, warning, setIsLoading]); // setCarrierLoads, setPendingLoadsCount are stable


  const getLoadsByUser = useCallback(() => {
    if (activeUser?.userIdMcld) {
      CarrierService.GetLoadsByUser(instance, activeUser.userIdMcld).then(res => {
        setCarrierLoads(res);

        let pendingCount = res.filter(({ status }) => status === "pending").length;
        setPendingLoadsCount(pendingCount > 0 ? pendingCount : null);
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    }
   }, [instance, activeUser?.userIdMcld, warning, setIsLoading]); // setCarrierLoads, setPendingLoadsCount are stable

  const getWindowTitle = () => {
    const paths = window.location.pathname.split("/");
    const id = searchParams.get("loadId") || searchParams.get("carrierId") || "";
    const action = searchParams.get("action");
    const type = searchParams.get("type");

    switch (paths[2]) {
      case "freight-matching":
        return "Suggested Freight Matching";
      case "quick-actions":
        return `${id}: Quick Actions`;
      case "load-booking":
        return `${id}: Booking Confirmation`;
      case "carrier-offer":
        return `${id}: Make Offer`;
      case "bounce-carrier":
        return `${id}: Bounce Carrier`;
      case "carrier-capacity":
        if (type === "preferred-lane")
          return action === "add-capacity" ? `${carrierName}: Add Preferred Lane` : `${carrierName}: Update Preferred Lane`;
        else if (type === "posted-truck")
          return action === "add-capacity" ? `${carrierName}: Add Posted Truck` : `${carrierName}: Update Posted Truck`;
        break;
      case "carrier-contact":
        return action === "add-contact" ? `${carrierName}: Add Carrier Contact` : `${carrierName}: Update Carrier Contact`;
      case "load-contact":
        return `${id}: Edit Load Contacts`;
      case "load-profile":
        return `${id}: Load Profile`;
      case "carrier-profile":
        return `${carrierName}: Carrier Profile`;
      case "carrier-search":
        return "Carrier Search";
      case "offer-boards":
        return "Carrier Offers Dashboard";
      default:
        return pendingLoadsCount ? `Phoenix (${pendingLoadsCount})` : "Phoenix";
    }
  };

  useEffect(() => {
    const title = getWindowTitle();
    document.title = title;

    let favicon = document.getElementById("favicon");
    favicon.href = pendingLoadsCount ? "/favicon-red.ico" : "/favicon.ico";
  }, [pendingLoadsCount, window.location, carrierName]);

  useEffect(() => {
    const carrierId = searchParams.get("carrierId");
    if (carrierId) {
      CarrierService.GetCarrier(instance, carrierId).then(res => {
        setCarrierName(`${formatTitleCase(res.name)}`);
      }).catch(error => {
        console.error(error);
      });
    }
  }, [window.location]);

  useEffect(() => {
    if (refreshLoads) {
      setRefreshLoads(false);
      if (isAdminUser) {
        getLoadsByRegion();
      } else if (isRepUser) {
        getLoadsByUser();
      }
    }
  }, [refreshLoads]);

  useEffect(() => {
   const getData = setTimeout(() => {
      if (isAdminUser) { // Only call if admin; getLoadsByRegion itself checks selectedRegions
        getLoadsByRegion();
      }
    }, 500);
    return () => clearTimeout(getData);
  }, [selectedRegions, selectedCustomers, isAdminUser, getLoadsByRegion]); // Added isAdminUser and getLoadsByRegion
 
  useEffect(() => {
    let isAdmin = securityCheck(userPermissions, "CarrierOffersDashboardManager", "Read");
    let isRep = securityCheck(userPermissions, "CarrierOffersDashboardRep", "Read");
    setIsAdminUser(isAdmin);
    setIsRepUser(isRep);
    
    if (isAdmin) {
     setIsLoading(true); // Set loading before async operations
      RegionService.GetRegions(instance).then(res => {
        const excludedRegionNames = ["Afterhours", "Bull Pen", "PCCS", "Partials"];
        const filteredRegions = res.filter(region => !excludedRegionNames.includes(region.regionName));
        setRegions(filteredRegions);
        // setIsLoading(false) will be handled by getLoadsByRegion or if default region selection leads to no loads.
      }).catch(error => {
        console.error(error);
        warning(error);
        setRegions([]); // Ensure regions is an array on error
        setIsLoading(false); // Stop loading on error if region fetch fails
      });
      getCustomers();
    } else if (isRep) {
      setIsLoading(true); // Set loading before async operations
      getLoadsByUser();
      } else {
      setIsLoading(false); // If neither admin nor rep, and not loading yet.  
    }
  }, [activeUser, userPermissions, instance, warning, getCustomers, getLoadsByUser, setIsLoading, setIsAdminUser, setIsRepUser]); // setRegions is stable

  // Effect to set default selected regions for admin if none are selected (e.g., from localStorage by OfferBoards.js)
  // after the list of available regions has been loaded.
  useEffect(() => {
    if (isAdminUser && regions.length > 0 && selectedRegions.length === 0 && activeUser?.regionId) {
      const userRegionExists = regions.some(region => region.regionId === activeUser.regionId);
      if (userRegionExists) {
        setSelectedRegions([activeUser.regionId]);
      } else if (regions.length > 0 && regions[0]) { // Ensure there's at least one region to select as a fallback
        setSelectedRegions([regions[0].regionId]);
      }
    }
  }, [regions, selectedRegions, isAdminUser, activeUser?.regionId, setSelectedRegions]);

  useEffect(() => {
    if (!connection && isLoggedIn) {
      const newConnection = new HubConnectionBuilder()
        .withUrl("/CarrierOffersNotifications")
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Information)
        .build();

      newConnection.onreconnected(() => {
        newConnection.invoke("AddToGroup", "CarrierOfferDashboard");
      });

      setConnection(newConnection);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    async function start() {
      try {
        if (connection.state === "Disconnected") {
          await connection.start();
        }

        if (!isLoggedIn) {
          await connection.invoke("RemoveFromGroup", "CarrierOfferDashboard");
        } else {
          await connection.invoke("AddToGroup", "CarrierOfferDashboard");
        }

        connection.on("CarrierOfferDashboard", update => {
          setRefreshLoads(true);
          setNewItem(update);
        });
      } catch (err) {
        console.error(err);
      }
    }

    const initiateConnections = async () => {
      if (connection && isLoggedIn) {
        start();
      } else if (connection && !isLoggedIn) {
        await connection.stop();
      }
    };

    initiateConnections();
  }, [connection, isLoggedIn]);

  return {
    isLoading,
    carrierLoads,
    newItem,
    pendingLoadsCount,
    setRefreshLoads,
    regions,
    selectedRegions,
    setSelectedRegions,
    isAdminUser,
    customers,
    selectedCustomers,
    setSelectedCustomers
  };
}